import classNames from 'classnames';
import { kebabCase } from '@utils/StringUtils';
import { WithMonthRangePicker, WithWeekRangePicker } from '@components/design';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { getWednesdayOfWeek } from '@utils/PeriodUtils';

export type PeriodSetter = (period: Date | undefined) => void;

export type RangePickerProps = {
  title?: string;
  from: Date | undefined;
  to: Date | undefined;
  setFrom: PeriodSetter;
  setTo: PeriodSetter;
  minDate: Date;
  maxDate: Date;
  disabled?: boolean;
  periodicity?: Periodicity;
  onBlur?: (event: any) => void;
};

const RangePicker: React.FC<RangePickerProps> = ({
  title,
  from,
  to,
  setFrom,
  setTo,
  minDate,
  maxDate,
  disabled = false,
  periodicity,
  onBlur,
}) => {
  const isMonthly = periodicity !== Periodicity.CustomWeeks;
  const PickerComponent = isMonthly ? WithMonthRangePicker : WithWeekRangePicker;
  const testIdSuffix = isMonthly ? 'date-range-picker' : 'week-range-picker';

  /**
   * Handles changes from the range picker and updates period state accordingly.
   * If the periodicity is custom weeks, it adjusts both dates to the Wednesday
   * of their respective weeks for maintaining consistent dates to avoid re-rendering.
   * (Week Range Picker does not provide consistent dates for the same week range)
   *
   * @param {[Date | null, Date | null]} range - Tuple of selected start and end dates.
   */
  const handleRangeChange = ([startDate, endDate]: [Date | null, Date | null]) => {
    const isValidDate = (date: Date | null): date is Date => date instanceof Date && !isNaN(date.getTime());

    if (!isValidDate(startDate) || !isValidDate(endDate)) return;

    if (!isMonthly) {
      setFrom(getWednesdayOfWeek(startDate));
      setTo(getWednesdayOfWeek(endDate));
    } else {
      setFrom(startDate);
      setTo(endDate);
    }
  };

  return (
    <div className="w-full" style={{ minWidth: '100%' }}>
      {title && <h2 className={classNames('mb-1', { 'opacity-75': disabled })}>{title}</h2>}
      <div data-testid={`${kebabCase(title)}-${testIdSuffix}`}>
        <PickerComponent
          name={kebabCase(title)}
          onBlur={onBlur}
          defaultDates={[from, to]}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          onChange={handleRangeChange}
        />
      </div>
    </div>
  );
};

export default RangePicker;
